import styled from 'styled-components';
import { Drawer } from 'react-rainbow-components';
import Card from 'components/Card';
import { color } from 'theme';
import { HealthScoreComponentStatus } from '../../types';
import getColorForStatus from '../../helpers/getColorForStatus';
import { GREEN_600, GREY_300, RED_500 } from '../../../../../constants';

const drawerTrendColor = (positive: boolean, goodWhen: 'positive' | 'negative') => {
    if (positive) {
        return goodWhen === 'positive' ? GREEN_600 : RED_500;
    }
    return goodWhen === 'positive' ? RED_500 : GREEN_600;
};

export const QuestionInsightContainer = styled(Card)`
    display: flex;
    flex: 1;
    width: 100%;
    align-items: stretch;
    justify-content: space-between;
    gap: 1.5rem;
`;

export const IconContainer = styled.div<{ $status: HealthScoreComponentStatus }>`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    width: 4.5rem;
    min-height: 140px;

    background: ${({ $status }) => getColorForStatus($status)};
    color: white;

    font-size: 1.125rem;
`;

export const QuestionInsights = styled.div<{ $status?: HealthScoreComponentStatus }>`
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    margin: 1.5rem 0;
    gap: 0.25rem;

    h3 {
        font-size: 1.125rem;
        font-weight: 600;
        color: ${color('text.main')};
    }

    h5 {
        font-size: 1.125rem;
        font-weight: 600;
        color: ${({ $status }) => getColorForStatus($status)};
    }

    p {
        font-size: 0.93rem;
        color: ${color('text.main')};
    }
`;

export const DetailsTriggerContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    padding-right: 1.5rem;
`;

export const PrimaryMetricsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
    max-width: 200px;
    height: fit-content;
    margin: 1.5rem 0;
    align-items: stretch;
    align-self: center;
`;

export const MetricRow = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.5rem;    
    border-radius: 0.5rem;
    border: 1px solid ${color('border.divider')};
`;

/** Shared label style: aggregate metrics, channel names, drawer rows. */
export const MetricLabel = styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.2;
    color: ${GREY_300};
`;

export const MetricValueInline = styled.span`
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem;
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.2;
    color: ${color('text.main')};
`;

export const MetricValueMuted = styled.span`
    font-size: 0.9375rem;
    font-weight: 500;
    line-height: 1.2;
    color: ${color('text.label')};
`;

export const HealthMetricHelpWrap = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    align-items: flex-start;
    line-height: 0;
`;

/**
 * Q5: flex **wrap** moves whole channel strips to the next row — not text inside a strip.
 */
export const ChannelsMetricsRow = styled.div`
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    gap: 0.75rem 1.25rem;
    width: 100%;
`;
export const ChannelStrip = styled(MetricRow)`
    flex: 0 0 auto;
    flex-shrink: 0;
    width: max-content;
    gap: 0.125rem;
    border-radius: 0.5rem;
    border: 1px solid ${color('border.divider')};


    & ${MetricValueInline}, & ${MetricValueMuted} {
        flex-wrap: nowrap;
        white-space: nowrap;
    }
`;

export const ChannelTitle = styled(MetricLabel)`
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-transform: capitalize;
`;

export const ChannelMetricLabeled = styled(MetricRow)`
    width: 100%;
`;

export const DetailsTrigger = styled.button`
    margin-top: 0.25rem;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 500;
    color: ${color('brand.main')};

    &:hover {
        text-decoration: underline;
    }
`;

export const StyledDrawer = styled(Drawer)`
    border-radius: 1.5rem;
    margin: 1.5rem;
    box-sizing: border-box;
    width: min(420px, calc(100vw - 3rem));
    min-width: min(320px, calc(100vw - 3rem));
    height: calc(100% - 3rem);
    min-height: calc(100% - 3rem);
    max-height: calc(100% - 3rem);
    box-shadow: 0 0 44px 0 rgba(0, 0, 0, 0.07);

    > div {
        box-sizing: border-box;
    }

    > div[id^='drawer-content'] {
        padding: 0.5rem 0 0;
        margin: 0 0 1rem;
    }
`;

export const DrawerHeader = styled.div`
    display: flex;
    margin: 1rem 1.5rem 0;
    padding-bottom: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.125rem;
    border-bottom: 1px solid ${color('border.divider')};
`;

export const DrawerTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 800;
    line-height: 1.75rem;
    color: ${color('text.main')};
    margin: 0;
`;

export const DrawerSubtitle = styled.p`
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.5rem;
    color: ${color('text.label')};
    margin: 0;
`;

export const DrawerBody = styled.div`
    padding: 1rem 1.5rem 1.5rem;
    overflow: auto;
`;

/** Aggregate drawer: stacked metric cards (matches legacy MetricsGroupWidget / MetricCard). */
export const DrawerMetricsStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

/** Global metrics block above per-channel sections in the channel insight drawer. */
export const DrawerAggregateSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1.25rem;
    border-bottom: 1px solid ${color('border.divider')};
`;

export const DrawerMetricCardRoot = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    padding: 0.5rem 0.625rem;
    border-radius: 0.5rem;
    border: 1px solid ${color('border.divider')};
`;

export const DrawerMetricCardTile = styled(DrawerMetricCardRoot)`
    flex: 1 1 calc(50% - 0.25rem);
    min-width: 9.5rem;
`;

export const DrawerMetricCardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
`;

export const DrawerMetricCardTitle = styled.span`
    flex: 1;
    min-width: 0;
    font-size: 0.75rem;
    font-weight: 500;
    color: ${color('text.label')};
    line-height: 1.25rem;
`;

export const DrawerMetricCardHelpWrap = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    align-items: flex-start;
    line-height: 0;
`;

export const DrawerMetricCardValueRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.5rem;
`;

export const DrawerMetricCardValue = styled.div`
    font-size: 1.125rem;
    font-weight: 700;
    color: ${color('text.main')};
    line-height: 1.35rem;
`;

export const DrawerMetricCardChange = styled.span<{ $positive: boolean; $goodWhen: 'positive' | 'negative' }>`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
    font-size: 0.875rem;
    line-height: 1rem;
    color: ${({ $positive, $goodWhen }) => drawerTrendColor($positive, $goodWhen)};
`;

export const DrawerMetricCardChangeIcon = styled.span`
    display: inline-flex;
    align-items: center;
    line-height: 0;

    svg {
        width: 14px;
        height: 14px;
    }
`;

/** Per-channel drawer sections (legacy ChannelInsights layout). */
export const ChannelDrawerSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-bottom: 1.25rem;
    margin-bottom: 1.25rem;
    border-bottom: 1px solid ${color('border.divider')};

    &:last-child {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
    }
`;

export const ChannelDrawerHeadingRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    min-width: 0;
`;

export const ChannelDrawerHeadingIconWrap = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    svg {
        display: block;
        width: 1.25rem;
        height: 1.25rem;
        flex-shrink: 0;
    }
`;

export const ChannelDrawerHeadingTitle = styled.h4`
    margin: 0;
    flex: 1;
    min-width: 0;
    font-size: 1rem;
    font-weight: 700;
    color: ${color('text.main')};
    text-transform: capitalize;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ChannelDrawerPieWrap = styled.div`
    width: 100%;
    height: 240px;
`;

export const ChannelDrawerTilesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

export const EmptyHint = styled.p`
    margin: 0;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.2;
    color: ${color('text.label')};
`;
